import { isSupportedAppointmentWorkingDraftTransition } from "@/app/booking/adapters/specialistAppointmentCompletionContext";
import {
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  createAppointmentStepOneDraftFingerprint,
  createAppointmentWorkingDraftFingerprint,
  isAppointmentDraftFingerprint,
} from "@/app/booking/core/appointmentDraftFingerprint";
import {
  appointmentTerminalPresentationCoversDraft,
  validateAppointmentTerminalPresentationSnapshot,
  type AppointmentTerminalPresentationSnapshotV1,
} from "@/app/booking/core/appointmentTerminalPresentation";

import {
  createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration,
  validateAppointmentWaitlistDraft,
  validateRetiredGroupAppointmentWaitlistDraftForStorageMigration,
  type AppointmentWaitlistDraftV3,
} from "./appointmentWaitlistDraft";
import {
  createAppointmentWaitlistIdempotencyKey,
  validateRetiredGroupAppointmentWaitlistProviderResultForStorageMigration,
  validateAppointmentWaitlistProviderResult,
  type AppointmentWaitlistSubmission,
} from "./appointmentWaitlistProvider";

export const APPOINTMENT_WAITLIST_RECEIPT_SCHEMA_VERSION = 3 as const;

export interface AppointmentWaitlistReceiptV3 {
  schemaVersion: typeof APPOINTMENT_WAITLIST_RECEIPT_SCHEMA_VERSION;
  kind: "appointment-waitlist-receipt";
  draftId: string;
  customerAccountId: string;
  idempotencyKey: string;
  sourceAppointmentDraftId: string;
  sourceAppointmentDraftSchemaVersion: 1;
  sourceAppointmentDraftFingerprint: string;
  workingAppointmentDraftFingerprint: string;
  presentationSnapshot: AppointmentTerminalPresentationSnapshotV1;
  waitlistDraft: AppointmentWaitlistDraftV3;
  submission: AppointmentWaitlistSubmission;
}

export type AppointmentWaitlistReceiptValidation =
  | { valid: true; receipt: AppointmentWaitlistReceiptV3 }
  | { valid: false; reason: "invalid-appointment-waitlist-receipt" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function validateAppointmentWaitlistReceiptForMode(
  value: unknown,
  expectedMode: AppointmentBookingDraftV1["mode"],
): AppointmentWaitlistReceiptValidation {
  if (
    !isRecord(value) ||
    value.schemaVersion !== APPOINTMENT_WAITLIST_RECEIPT_SCHEMA_VERSION ||
    value.kind !== "appointment-waitlist-receipt" ||
    typeof value.idempotencyKey !== "string" ||
    typeof value.sourceAppointmentDraftId !== "string" ||
    value.sourceAppointmentDraftSchemaVersion !== 1 ||
    !isAppointmentDraftFingerprint(value.sourceAppointmentDraftFingerprint) ||
    !isAppointmentDraftFingerprint(value.workingAppointmentDraftFingerprint) ||
    !isRecord(value.submission) ||
    typeof value.submission.submittedAtEpochMs !== "number" ||
    !Number.isSafeInteger(value.submission.submittedAtEpochMs) ||
    value.submission.submittedAtEpochMs < 0 ||
    typeof value.submission.acceptedAtEpochMs !== "number" ||
    !Number.isSafeInteger(value.submission.acceptedAtEpochMs) ||
    value.submission.acceptedAtEpochMs < 0
  ) {
    return {
      valid: false,
      reason: "invalid-appointment-waitlist-receipt",
    };
  }

  const submittedAtEpochMs = value.submission.submittedAtEpochMs;
  const acceptedAtEpochMs = value.submission.acceptedAtEpochMs;
  const draftValidation =
    expectedMode === "individual"
      ? validateAppointmentWaitlistDraft(value.waitlistDraft)
      : validateRetiredGroupAppointmentWaitlistDraftForStorageMigration(
          value.waitlistDraft,
        );
  const presentationSnapshot = validateAppointmentTerminalPresentationSnapshot(
    value.presentationSnapshot,
  );

  if (
    !draftValidation.valid ||
    !presentationSnapshot ||
    draftValidation.draft.customerAccountId === null ||
    value.draftId !== draftValidation.draft.draftId ||
    value.sourceAppointmentDraftId !==
      draftValidation.draft.appointmentDraft.draftId ||
    value.customerAccountId !== draftValidation.draft.customerAccountId
  ) {
    return {
      valid: false,
      reason: "invalid-appointment-waitlist-receipt",
    };
  }

  const expectedIdempotencyKey =
    expectedMode === "individual"
      ? createAppointmentWaitlistIdempotencyKey(draftValidation.draft, {
          evaluatedAtEpochMs: submittedAtEpochMs,
        })
      : createRetiredGroupAppointmentWaitlistDraftIdempotencyKeyForStorageMigration(
          draftValidation.draft,
        );
  const providerResultInput = {
    draft: draftValidation.draft,
    idempotencyKey: value.idempotencyKey,
    result: {
      ok: true as const,
      submission: value.submission,
    },
    acceptedNotBeforeEpochMs: submittedAtEpochMs,
    evaluatedAtEpochMs: acceptedAtEpochMs,
  };
  const providerResult =
    expectedMode === "individual"
      ? validateAppointmentWaitlistProviderResult(providerResultInput)
      : validateRetiredGroupAppointmentWaitlistProviderResultForStorageMigration(
          providerResultInput,
        );
  const workingAppointmentDraftFingerprint =
    createAppointmentWorkingDraftFingerprint(
      draftValidation.draft.appointmentDraft,
    );

  if (
    !expectedIdempotencyKey ||
    !workingAppointmentDraftFingerprint ||
    value.idempotencyKey !== expectedIdempotencyKey ||
    value.workingAppointmentDraftFingerprint !==
      workingAppointmentDraftFingerprint ||
    !appointmentTerminalPresentationCoversDraft({
      draft: draftValidation.draft.appointmentDraft,
      presentationSnapshot,
    }) ||
    !providerResult.ok
  ) {
    return {
      valid: false,
      reason: "invalid-appointment-waitlist-receipt",
    };
  }

  return {
    valid: true,
    receipt: {
      schemaVersion: APPOINTMENT_WAITLIST_RECEIPT_SCHEMA_VERSION,
      kind: "appointment-waitlist-receipt",
      draftId: draftValidation.draft.draftId,
      customerAccountId: draftValidation.draft.customerAccountId,
      idempotencyKey: expectedIdempotencyKey,
      sourceAppointmentDraftId: draftValidation.draft.appointmentDraft.draftId,
      sourceAppointmentDraftSchemaVersion: 1,
      sourceAppointmentDraftFingerprint:
        value.sourceAppointmentDraftFingerprint,
      workingAppointmentDraftFingerprint,
      presentationSnapshot,
      waitlistDraft: draftValidation.draft,
      submission: providerResult.submission,
    },
  };
}

export function validateAppointmentWaitlistReceipt(
  value: unknown,
): AppointmentWaitlistReceiptValidation {
  return validateAppointmentWaitlistReceiptForMode(value, "individual");
}

export function validateRetiredGroupAppointmentWaitlistReceiptForStorageMigration(
  value: unknown,
): AppointmentWaitlistReceiptValidation {
  return validateAppointmentWaitlistReceiptForMode(value, "group");
}

function createAppointmentWaitlistReceiptForMode(input: {
  sourceAppointmentDraft: AppointmentBookingDraftV1;
  presentationSnapshot: AppointmentTerminalPresentationSnapshotV1;
  waitlistDraft: AppointmentWaitlistDraftV3;
  idempotencyKey: string;
  submission: AppointmentWaitlistSubmission;
  expectedMode: AppointmentBookingDraftV1["mode"];
}) {
  const submittedAtEpochMs = input.submission.submittedAtEpochMs;
  const acceptedAtEpochMs = input.submission.acceptedAtEpochMs;
  const currentDraftValidation =
    input.expectedMode === "individual"
      ? validateAppointmentWaitlistDraft(input.waitlistDraft)
      : validateRetiredGroupAppointmentWaitlistDraftForStorageMigration(
          input.waitlistDraft,
        );
  const currentProviderResult = currentDraftValidation.valid
    ? input.expectedMode === "individual"
      ? validateAppointmentWaitlistProviderResult({
          draft: currentDraftValidation.draft,
          idempotencyKey: input.idempotencyKey,
          result: {
            ok: true,
            submission: input.submission,
          },
          acceptedNotBeforeEpochMs: submittedAtEpochMs,
          evaluatedAtEpochMs: acceptedAtEpochMs,
        })
      : validateRetiredGroupAppointmentWaitlistProviderResultForStorageMigration(
          {
            draft: currentDraftValidation.draft,
            idempotencyKey: input.idempotencyKey,
            result: {
              ok: true,
              submission: input.submission,
            },
            acceptedNotBeforeEpochMs: submittedAtEpochMs,
            evaluatedAtEpochMs: acceptedAtEpochMs,
          },
        )
    : null;
  const sourceValidation = validateAppointmentStepOneBookingDraft(
    input.sourceAppointmentDraft,
  );
  const presentationSnapshot = validateAppointmentTerminalPresentationSnapshot(
    input.presentationSnapshot,
  );
  const sourceAppointmentDraftFingerprint =
    createAppointmentStepOneDraftFingerprint(input.sourceAppointmentDraft);
  const workingAppointmentDraftFingerprint =
    createAppointmentWorkingDraftFingerprint(
      input.waitlistDraft.appointmentDraft,
    );
  if (
    !currentDraftValidation.valid ||
    !currentProviderResult?.ok ||
    !sourceValidation.valid ||
    !presentationSnapshot ||
    !sourceAppointmentDraftFingerprint ||
    !workingAppointmentDraftFingerprint ||
    sourceValidation.draft.draftId !==
      input.waitlistDraft.appointmentDraft.draftId ||
    !isSupportedAppointmentWorkingDraftTransition({
      sourceDraft: sourceValidation.draft,
      workingDraft: input.waitlistDraft.appointmentDraft,
      staffChoiceEvidence: input.waitlistDraft.staffChoiceEvidence,
      timeProfessionalMutationEvidence:
        input.waitlistDraft.timeProfessionalMutationEvidence,
      evaluatedAtEpochMs: submittedAtEpochMs,
    })
  ) {
    return null;
  }

  const validation = validateAppointmentWaitlistReceiptForMode({
    schemaVersion: APPOINTMENT_WAITLIST_RECEIPT_SCHEMA_VERSION,
    kind: "appointment-waitlist-receipt",
    draftId: input.waitlistDraft.draftId,
    customerAccountId: input.waitlistDraft.customerAccountId,
    idempotencyKey: input.idempotencyKey,
    sourceAppointmentDraftId: sourceValidation.draft.draftId,
    sourceAppointmentDraftSchemaVersion: 1,
    sourceAppointmentDraftFingerprint,
    workingAppointmentDraftFingerprint,
    presentationSnapshot,
    waitlistDraft: input.waitlistDraft,
    submission: input.submission,
  }, input.expectedMode);

  return validation.valid ? validation.receipt : null;
}


export function createAppointmentWaitlistReceipt(input: {
  sourceAppointmentDraft: AppointmentBookingDraftV1;
  presentationSnapshot: AppointmentTerminalPresentationSnapshotV1;
  waitlistDraft: AppointmentWaitlistDraftV3;
  idempotencyKey: string;
  submission: AppointmentWaitlistSubmission;
}) {
  return createAppointmentWaitlistReceiptForMode({
    ...input,
    expectedMode: "individual",
  });
}

export function createRetiredGroupAppointmentWaitlistReceiptForStorageMigration(
  input: {
    sourceAppointmentDraft: AppointmentBookingDraftV1;
    presentationSnapshot: AppointmentTerminalPresentationSnapshotV1;
    waitlistDraft: AppointmentWaitlistDraftV3;
    idempotencyKey: string;
    submission: AppointmentWaitlistSubmission;
  },
) {
  return createAppointmentWaitlistReceiptForMode({
    ...input,
    expectedMode: "group",
  });
}
